import axios from "../lib/apiAxios";
import config from "../config";
import store from "../store";

/**
 * The key used to store the original location in localStorage, so we can load the view the user originally
 * requested after the login flow.
 */
export const ORIGINAL_LOCATION_KEY = "invaiolab-original-location";

/**
 * Call the /auth/sso lab-api endpoint to verify the authorization code returned from the SSO flow
 * and set the cookie/session if the auth is valid for the user.
 */
function verifyAuthorizationCode(authorizationToken) {
    return axios.get(
        `/auth/sso?code=${authorizationToken}&redirect_uri=${document.location.origin}`
    );
}

export function getAuthSSOLoginUrl(cognitoDomain, cognitoClientId) {
    return (
        `https://${cognitoDomain}.auth.us-east-1.amazoncognito.com/login?` +
        `client_id=${cognitoClientId}&` +
        "response_type=code&scope=email+openid+phone+profile&" +
        `redirect_uri=${document.location.origin}`
    );
}

/**
 * After successful auth flow, this returns the URL to redirect the browser to. This should load up
 * the original location the user was trying to access, and display the application UI.
 */
function getSuccessfulAuthRedirect() {
    const previousLocation = localStorage.getItem(ORIGINAL_LOCATION_KEY);
    localStorage.removeItem(ORIGINAL_LOCATION_KEY);
    // redirect away from /sso to wherever the user was going
    return previousLocation || "/";
}

/**
 * Handle an error from the authorization code verification flow. This will set the auth error in the store
 * so it can be displayed in the App UI
 */
function handleAuthorizationCodeVerificationError(err) {
    console.log("sso failed", err);
    return store.dispatch("setAuthError", err.response.data.error);
}

/**
 * Handle the authorization code returned from the SSO flow. This calls lab-api to verify the code and exchange it
 * and then redirects the user to the original location they were trying to access. When successful, the returned
 * Promise is resolved with the URL to redirect the browser to (away from /sso with the authorization token in
 * the URL to the original location).
 *
 * If something goes wrong (invalid authorization_code) the Promise returned by this function will be rejected.
 */
export function handleSSOAuthorizationCode(authorizationCode) {
    return new Promise((resolve, reject) => {
        verifyAuthorizationCode(authorizationCode)
            .then(() => {
                const path = getSuccessfulAuthRedirect();
                resolve(path);
            })
            .catch((err) => {
                handleAuthorizationCodeVerificationError(err).then(() => {
                    reject(err);
                });
            });
    });
}

/**
 * Check the auth state of the user. This will call the server to check if the user is authenticated, and
 * sets the user state in the store. The Promise returned by this function resolves with an object of
 * type: { isAuthenticated: boolean }
 */
export function checkUserAuthState() {
    return new Promise((resolve) => {
        // regardless of whether the auth state resolves successfully, we want to resolve this promise
        store.dispatch("checkAuthState").finally(() => {
            resolve({ isAuthenticated: store.getters.isAuthenticated });
        });
    });
}

/**
 * Handle app initialization, checking the auth states. Returns a Promise that resolves with no
 * arguments indicating the app can load up. Rejects to the URL to redirect the browser for the
 * user to sign in.
 */
export function handleAppInitialization() {
    // store where they were going
    localStorage.setItem(ORIGINAL_LOCATION_KEY, window.location);
    return axios.get(`/auth/enabled`).then((response) => {
        return store.dispatch("setAuthConfig", response.data).then(() => {
            return checkUserAuthState();
        });
    });
}

/**
 * Fetch the currently authenticated user from the server.
 */
export function fetchAuthUser() {
    return axios.get(`/auth/login`).then((response) => {
        return response.data;
    });
}

export function refreshSession() {
    return axios.post(`${config.apiHost}/auth/refreshSession`).then((response) => {
        return response.data;
    });
}

/**
 * This is the amount of time we should warn them that they will be logged out.
 * @type {number}
 */
const SESSION_EXPIRATION_WARNING_MINUTES = 5;

/**
 * This is the amount of time the user can be idle before they are logged out.
 * TODO: make this configurable based on the type of workstation: public, private, etc.
 */
export const IDLE_TIMEOUT_MINUTES = 60;

function getSecondsUntilSessionExpiration(sessionExpiresAt) {
    const now = new Date();
    return (sessionExpiresAt.getTime() - now.getTime()) / 1000;
}

export function isNearSessionExpiration(sessionExpiresAt) {
    const timeUntilSessionExpires = getSecondsUntilSessionExpiration(sessionExpiresAt);
    return timeUntilSessionExpires / 60 < SESSION_EXPIRATION_WARNING_MINUTES;
}

export function isSessionExpired(sessionExpiresAt) {
    const secondsUntilSessionExpires = getSecondsUntilSessionExpiration(sessionExpiresAt);
    return secondsUntilSessionExpires <= 0;
}

export function createInactivityCheck(store, handler) {
    // TODO: can I read the state from the store? yeah right?
    let lastActiveTimestamp = -1;

    ["mousemove", "keydown", "click", "scroll"].forEach((event) => {
        handler(event, () => {
            const now = Date.now();
            const idleDuration = now - lastActiveTimestamp;
            if (idleDuration < 30000) {
                // prevent the inactivity check from updating the store multiple times in quick succession
                return;
            }
            lastActiveTimestamp = Date.now();
            store.dispatch("updateLastActiveTime", lastActiveTimestamp);
        });
    });
}

/**
 * Log the user out, clears the session/cookie.
 */
export function doLogout() {
    return axios.post(`/auth/logout`);
}

function getPermissions(user) {
    return user.groups
        .map((group) => {
            return group.role.permissions;
        })
        .flat();
}

function isAllowed(requiredPermission, userPermissions) {
    return (
        userPermissions.find((userPermission) => {
            return (
                userPermission.resource === requiredPermission.resource &&
                (userPermission.action === "*" ||
                    userPermission.action === requiredPermission.action)
            );
        }) !== undefined
    );
}

/**
 * Note that this doesn't take into account whether auth is enabled or not (because it doesn't have
 * access to the store). Components should use the `isAuthorized` getter from the store.
 */
export function isAuthorized(permissionString, user) {
    return isAuthorizedForPermissions({ oneOf: [permissionString] }, user);
}

export function isAuthorizedForPermissions(requiredPermissions, user) {
    if (!user) {
        return false;
    }
    const userPermissions = getPermissions(user);

    if (requiredPermissions.oneOf.length === 0) {
        return true;
    }

    for (const requiredPermissionString of requiredPermissions.oneOf) {
        const split = requiredPermissionString.split(":");
        const requiredPermission = {
            resource: split[0],
            action: split[1],
        };

        if (isAllowed(requiredPermission, userPermissions)) {
            return true;
        }
    }

    return false;
}

export function createSessionTimer({
    state,
    sessionRefreshCallback,
    sessionExpiringCallback,
    sessionExpiredCallback,
}) {
    if (state.sessionIdleTimer) {
        clearInterval(state.sessionIdleTimer);
    }

    return setInterval(() => {
        if (isSessionExpired(state.sessionExpiresAt)) {
            console.log("Session expired");
            clearInterval(state.sessionIdleTimer);
            sessionExpiredCallback();
        } else if (isNearSessionExpiration(state.sessionExpiresAt)) {
            const now = Date.now();
            const idleDuration = now - state.lastActiveTime;
            if (idleDuration < IDLE_TIMEOUT_MINUTES * 1000 * 60) {
                // If the user has not been idle for too long, refresh the session
                refreshSession()
                    .then((authUser) => {
                        sessionRefreshCallback(authUser);
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            } else {
                sessionExpiringCallback();
            }
        }
    }, 5000);
}
